import { useCallback, useRef, useState } from "react";
import { api } from "../api/client.js";
import { haptic } from "../telegram/sdk.js";
import type { Difficulty } from "@cslame/shared";

export type GameUiState =
  | { phase: "idle"; lastMultiplier?: number }
  | {
      phase: "active";
      roundId: string;
      stake: number;
      difficulty: Difficulty;
      seedHash: string;
      revealed: string;
      picks: number;
      multiplier: number;
      nextMultiplier: number | null;
      dangerDigits: Set<number>;
      flashDigit: { digit: number; tone: "safe" | "bust" } | null;
    }
  | {
      phase: "cashed_out";
      payout: number;
      multiplier: number;
      revealed: string;
    }
  | {
      phase: "busted";
      stake: number;
      position: number;
      bombDigits: number[];
      revealed: string;
    };

export function useGame(onBalanceChange: (b: number) => void) {
  const [state, setState] = useState<GameUiState>({ phase: "idle" });
  const [error, setError] = useState<string | null>(null);
  const [shaking, setShaking] = useState(false);
  const [explosion, setExplosion] = useState(false);
  const inflight = useRef(false);

  const triggerBoom = useCallback(() => {
    setShaking(true);
    setExplosion(true);
    setTimeout(() => setShaking(false), 620);
  }, []);

  const start = useCallback(
    async (stake: number, difficulty: Difficulty) => {
      if (inflight.current) return;
      inflight.current = true;
      setError(null);
      setExplosion(false);
      try {
        const res = await api.startRound({ stake, difficulty });
        const bal = await api.balance();
        onBalanceChange(bal.balance);
        setState({
          phase: "active",
          roundId: res.roundId,
          stake: res.stake,
          difficulty: res.difficulty,
          seedHash: res.seedHash,
          revealed: "",
          picks: 0,
          multiplier: 1,
          nextMultiplier: null,
          dangerDigits: new Set(),
          flashDigit: null,
        });
      } catch (e) {
        setError((e as Error).message);
      } finally {
        inflight.current = false;
      }
    },
    [onBalanceChange],
  );

  const press = useCallback(async (digit: number) => {
    if (inflight.current) return;
    inflight.current = true;
    setState((prev) => {
      if (prev.phase !== "active") return prev;
      return { ...prev };
    });
    haptic.tick();
    try {
      setState((prev) => {
        if (prev.phase !== "active") return prev;
        return { ...prev, flashDigit: null };
      });
      const s = state;
      if (s.phase !== "active") return;
      const res = await api.press(s.roundId, digit);
      if (res.safe) {
        haptic.tap();
        setState((prev) => {
          if (prev.phase !== "active") return prev;
          return {
            ...prev,
            revealed: res.revealed,
            picks: s.picks + 1,
            multiplier: res.multiplier,
            nextMultiplier: res.nextMultiplier,
            dangerDigits: new Set(), // reset for next position
            flashDigit: { digit, tone: "safe" },
          };
        });
      } else {
        haptic.bust();
        triggerBoom();
        setState({
          phase: "busted",
          stake: s.stake,
          position: res.position,
          bombDigits: res.bombDigits,
          revealed: res.revealed,
        });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      inflight.current = false;
    }
  }, [state, triggerBoom]);

  const cashout = useCallback(async () => {
    if (inflight.current) return;
    if (state.phase !== "active") return;
    if (state.picks < 1) return;
    inflight.current = true;
    try {
      const res = await api.cashout(state.roundId);
      haptic.win();
      onBalanceChange(res.balance);
      setState({
        phase: "cashed_out",
        payout: res.payout,
        multiplier: res.multiplier,
        revealed: res.revealed,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      inflight.current = false;
    }
  }, [state, onBalanceChange]);

  const reset = useCallback(() => {
    setExplosion(false);
    setShaking(false);
    setState({ phase: "idle" });
    setError(null);
  }, []);

  return {
    state,
    error,
    shaking,
    explosion,
    setExplosion,
    start,
    press,
    cashout,
    reset,
  };
}
